import { safeCompileRegExp } from 'utils';
import type { CustomRegisterOptions } from 'components/form';

export const CARD_WITH_SCANNABLE_NUMBER = 'NTAG 216';

/**
 * The largest card number a card can carry, keyed by
 * `WorkerCardFormat.encoding`. A Corporate 1000 48-bit card holds a 23-bit
 * card number, and the Avigilon connector rejects anything larger
 * (`avigilonCredentialSync.ts` in the API's connectors module).
 */
const cardNumberLimitByEncoding: Record<
  string,
  { formatName: string; maximum: number }
> = {
  'wiegand-48-bit': { formatName: 'Corporate 1000', maximum: 2 ** 23 - 1 },
};

/**
 * Validation for a badge number entered for a card format: the format's
 * configured pattern, plus the largest card number its encoding can carry.
 */
export const getBadgeNumberValidation = <TFields>({
  validationRegex,
  validationMessage,
  encoding,
}: {
  validationRegex?: string | null;
  validationMessage?: string | null;
  encoding?: string | null;
}): CustomRegisterOptions<TFields> => {
  const cardNumberLimit = encoding
    ? cardNumberLimitByEncoding[encoding]
    : undefined;

  return {
    required: true,
    pattern: validationRegex
      ? {
          value: safeCompileRegExp(validationRegex),
          message: validationMessage ?? 'Please enter a valid badge number.',
        }
      : undefined,
    validate: cardNumberLimit
      ? (value: string): true | string => {
          const cardNumber = value?.trim() ?? '';
          return (
            (/^\d+$/.test(cardNumber) &&
              Number(cardNumber) >= 1 &&
              Number(cardNumber) <= cardNumberLimit.maximum) ||
            `${
              cardNumberLimit.formatName
            } badge numbers must be a whole number from 1 to ${cardNumberLimit.maximum.toLocaleString(
              'en-US',
            )}.`
          );
        }
      : undefined,
  };
};
